import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from "@mui/material";
import {useTranslation} from "react-i18next";
import {
    FloorplanReservationItemDTO,
} from "crqlar-react-common/models/floorplan/floorplanReservationItem.dto";
import {formatDateHMRange} from "crqlar-react-common/dist/utils/date.util";
import ReservationPreviewCardContent from "./ReservationPreviewCardContent";
import SaveWithEmailSplitButton from "./SaveWithEmailSplitButton";
import {buildPreviewCardProps, dropDecidesGuestEmail} from "../../utils/drop-utils";
import {isConflictCompleted} from "../../utils/reservation-utils";
import {DragData} from "../../types";
import {useReadStore} from "../../hooks/useReadStore";

interface Props {
    open: boolean;
    onClose: () => void;
    onConfirm: (sendEmail: boolean) => void;
    conflicts: FloorplanReservationItemDTO[];
    targetTableName: string;
    dragData: DragData;
}

const SharedTableConfirmDialog: React.FC<Props> = ({
                                                       open,
                                                       onClose,
                                                       onConfirm,
                                                       conflicts,
                                                       targetTableName,
                                                       dragData,
                                                   }) => {
    const {t} = useTranslation();
    const floorplanTables = useReadStore(s => s.floorplanTables);
    const previewCardProps = buildPreviewCardProps(dragData, targetTableName, floorplanTables);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{t('Table is occupied')}</DialogTitle>

            <DialogContent>
                <Box sx={{mb: 2.5}}>
                    <ReservationPreviewCardContent {...previewCardProps} />
                </Box>

                <Alert severity="info" variant="outlined">
                    <AlertTitle sx={{fontWeight: 600}}>
                        {t('Another guest is already seated at this table')}
                    </AlertTitle>
                    {conflicts.map((c) => {
                        const profile = c.profiles?.[0];
                        const name = profile
                            ? [profile.lastName, profile.firstName].filter(Boolean).join(' ')
                            : t('Unknown guest');
                        const res = c.restaurantReservation;
                        const completed = isConflictCompleted(c);
                        return (
                            <Box key={c.reservationId} sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: 2,
                                py: 0.25
                            }}>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}>
                                    <Typography variant="body2">{name}</Typography>
                                    {completed && (
                                        <Chip
                                            label={t('Already ended')}
                                            size="small"
                                            color="default"
                                            sx={{height: 20, fontSize: '0.7rem'}}
                                        />
                                    )}
                                </Box>
                                <Typography variant="body2" fontWeight={600}
                                            color={completed ? 'text.secondary' : 'info.dark'}
                                            sx={{whiteSpace: 'nowrap'}}>
                                    {formatDateHMRange(res?.expectedStartDate, res?.expectedEndDate)}
                                </Typography>
                            </Box>
                        );
                    })}
                </Alert>
            </DialogContent>

            <DialogActions sx={{px: 3, pb: 2}}>
                <Button onClick={onClose}>{t('Cancel')}</Button>
                {/* The dropdown is the only way to notify the guest — a plain move stays silent. */}
                <SaveWithEmailSplitButton
                    label={t('Move anyway')}
                    onConfirm={onConfirm}
                    showEmailOption={dropDecidesGuestEmail(dragData)}
                />
            </DialogActions>
        </Dialog>
    );
};

export default SharedTableConfirmDialog;
